import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TheoryMarkdownProps {
  content: string;
  initialHtml?: string | null;
}

export const TheoryMarkdown = React.memo<TheoryMarkdownProps>(
  ({ content, initialHtml }) => {
    if (initialHtml) {
      return (
        <div
          className="markdown-theory"
          id="theory-markdown-content"
          dangerouslySetInnerHTML={{ __html: initialHtml }}
        />
      );
    }

    return (
      <div className="markdown-theory" id="theory-markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4 dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol dir="auto" style={{ textAlign: 'start' }}>
                {children}
              </ol>
            ),
            li: ({ children }) => <li dir="auto">{children}</li>,
            table: ({ children }) => (
              <div className="theory-table-wrapper" dir="auto">
                <table>{children}</table>
              </div>
            ),
            blockquote: ({ children }) => {
              // Check if blockquote content has [!NOTE], [!TIP], [!WARNING], [!IMPORTANT], [!EXAM]
              const textContent = extractText(children);
              const calloutMatch = textContent.match(/^\s*\[!(NOTE|TIP|WARNING|IMPORTANT|EXAM)\]\s*(.*)$/m);

              if (calloutMatch) {
                const type = calloutMatch[1].toUpperCase();
                const customTitle = calloutMatch[2].trim();

                let icon = '📝';
                let defaultTitle = 'Note';
                let className = 'callout-note';

                if (type === 'TIP') {
                  icon = '💡';
                  defaultTitle = 'Astuce / Tip';
                  className = 'callout-tip';
                } else if (type === 'WARNING') {
                  icon = '⚠️';
                  defaultTitle = 'Attention / Warning';
                  className = 'callout-warning';
                } else if (type === 'IMPORTANT') {
                  icon = '❗';
                  defaultTitle = 'Important';
                  className = 'callout-important';
                } else if (type === 'EXAM') {
                  icon = '🎯';
                  defaultTitle = 'Point d’Examen / Exam Alert';
                  className = 'callout-exam';
                }

                // Filter out the [!TYPE] marker line from the rendered elements
                const cleanedChildren = removeCalloutHeader(children, calloutMatch[0]);

                return (
                  <div className={`callout-box ${className}`} dir="auto">
                    <div className="callout-header">
                      <span>{icon}</span>
                      <span>{customTitle || defaultTitle}</span>
                    </div>
                    <div className="callout-body">{cleanedChildren}</div>
                  </div>
                );
              }

              return (
                <blockquote
                  dir="auto"
                  style={{
                    textAlign: 'start',
                    borderInlineStart: '4px solid var(--line)',
                    paddingInlineStart: '1rem',
                    margin: '1rem 0',
                    color: 'var(--mute)',
                    fontStyle: 'italic',
                  }}
                >
                  {children}
                </blockquote>
              );
            },
            img: ({ src, alt }) => (
              <img src={src} alt={alt || 'Figure'} loading="lazy" referrerPolicy="no-referrer" />
            ),
            code: ({ children }) => <code>{children}</code>,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  },
  (prev, next) => prev.content === next.content && prev.initialHtml === next.initialHtml
);

// Helper to extract plain text string from React children to check for callout prefixes
function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (React.isValidElement(node) && node.props && (node.props as Record<string, unknown>).children) {
    return extractText((node.props as Record<string, unknown>).children as React.ReactNode);
  }
  return '';
}

// Remove the `[!TYPE] Title` string from the first child text so it's not rendered twice
function removeCalloutHeader(node: React.ReactNode, matchedPrefix: string): React.ReactNode {
  if (!Array.isArray(node)) {
    return filterNode(node, matchedPrefix);
  }
  return node.map((child, idx) => {
    if (idx === 0) return filterNode(child, matchedPrefix);
    return child;
  });
}

function filterNode(node: React.ReactNode, matchedPrefix: string): React.ReactNode {
  if (typeof node === 'string') {
    return node.replace(matchedPrefix, '').trim();
  }
  if (React.isValidElement(node) && node.props) {
    const props = node.props as Record<string, unknown>;
    if (props.children) {
      return React.cloneElement(node, {
        ...props,
        children: removeCalloutHeader(props.children as React.ReactNode, matchedPrefix),
      } as Record<string, unknown>);
    }
  }
  return node;
}
